export function renderInvoiceESCPOS(invoice, org, cfg) {
  const ESC = '\x1B';
  const GS  = '\x1D';
  const LINE_WIDTH = 48;
  let out = [];

  /* =====================
     Helpers
  ====================== */

  const init       = () => ESC + '@';
  const align      = (n) => ESC + 'a' + String.fromCharCode(n); // 0 left,1 center,2 right
  const boldOn     = () => ESC + 'E' + '\x01';
  const boldOff    = () => ESC + 'E' + '\x00';
  const feed       = (n = 1) => '\n'.repeat(n);
  const cut        = () => GS + 'V' + '\x00';
  const hr         = () => '-'.repeat(LINE_WIDTH) + '\n';
  const fontDoubleOn  = () => GS + '!' + '\x11'; // double width + height
  const fontNormal   = () => GS + '!' + '\x00';

  const totalsLine = (label, value) => {
    const LABEL_W = 12;   // width of label column
    const VALUE_W = 12;   // width of value column

    const labelStr = label.padStart(LABEL_W);
    const valueStr = value.padStart(VALUE_W);

    const leftPad = LINE_WIDTH - (LABEL_W + VALUE_W);

    return (
      ' '.repeat(leftPad) +
      labelStr +
      valueStr +
      '\n'
    );
  };

  const push = (data) => {
    for (let i = 0; i < data.length; i++) {
      out.push(data.charCodeAt(i));
    }
  };

  const base64ToBinary = (b64) => {
    const raw = atob(b64);
    let out = '';
    for (let i = 0; i < raw.length; i++) {
      out += String.fromCharCode(raw.charCodeAt(i));
    }

    return out;
  };


  const colLine = (qty, unit, total) => {
    const qtyStr   = qty.toString();
    const unitStr  = unit.toString();
    const totalStr = total.toString();

    // Column widths (must sum <= LINE_WIDTH)
    const QTY_W   = 8;
    const UNIT_W  = 12;
    const TOTAL_W = 12;

    const leftPad = LINE_WIDTH - (QTY_W + UNIT_W + TOTAL_W);
    
    return (
        ' '.repeat(leftPad) +
        qtyStr.padStart(QTY_W) +
        unitStr.padStart(UNIT_W) +
        totalStr.padStart(TOTAL_W) +
        '\n'
    );
  };

  /* =====================
     Header (config driven)
  ====================== */

  push( init());
  // push( setLeftMargin(24);
  const headerConfig = (cfg.receipt_header || [])
    .filter(h => h.enabled)
    .sort((a, b) => a.order - b.order);

  const logoItem = headerConfig.find(h => h.key === 'logo');
  const nameItem = headerConfig.find(h => h.key === 'pharmacy_name');

  const inlineLogoWithName =
    logoItem &&
    nameItem &&
    logoItem.order === nameItem.order;

  const hasAddress =
    headerConfig.some(h => h.key === 'address' && h.enabled) &&
    !!org?.address;

  if (
    inlineLogoWithName &&
    org?.org_logo_print &&
    org?.logo_escpos_base64
  ) {
    // 🔹 INLINE HEADER BLOCK (logo first, name second)
    push(align(1));
    push(base64ToBinary(org.logo_escpos_base64));

    push(fontDoubleOn());
    push(boldOn());
    push(org.organization_name + '\n');
    push(boldOff());
    push(fontNormal());
    push(align(0));

    if (!hasAddress) {
      push(feed(1));
    }
  }

  headerConfig.forEach(h => {
    // Skip logo + name if inline mode handled already
    if (
      inlineLogoWithName &&
      (h.key === 'logo' || h.key === 'pharmacy_name')
    ) {
      return;
    }

    switch (h.key) {
      case 'logo':
        if (org?.org_logo_print && org?.logo_escpos_base64) {
          push(align(1));
          push(base64ToBinary(org.logo_escpos_base64));
          push(feed(1));
        }
        break;

      case 'pharmacy_name':
        push(align(1));
        push(fontDoubleOn());
        push(boldOn());
        push(org.organization_name + '\n');
        push(boldOff());
        push(fontNormal());
        push(align(0));
        if (!hasAddress) {
          push(feed(1));
        }
        break;


      case 'address':
        push( align(1));
        push( (org?.address || '') + '\n');
        push( align(0));
        push( feed(1));
        break;

      case 'contact':
        push( `Tel: ${org?.contact_number||''}\n`);
        break;

      case 'department':
        push( `Branch: ${invoice.department_name}\n`);
        break;

      case 'date':
        push( `Date: ${new Date(invoice.date).toLocaleString()}\n`);
        break;
      
      case 'operator':
        push( `Operator: ${invoice.operator_name}\n`);
        break;

      case 'customer':
        push( `Customer: ${invoice.invoiced_customer || 'Walk-in'}\n`);
        break;

      default:
        break;
    }
  });

  push( hr());

  /* =====================
     Invoice Meta
  ====================== */

  push( boldOn());
  push( `Invoice: ${invoice.invoice_number}\n`);
  push( boldOff());

  push( hr());

  /* =====================
     Item Table
  ====================== */

  push( boldOn());
  push( 'ITEM');
  push( ' '.repeat(48 - (4 + 8 + 12 + 12)) +
       'QTY'.padStart(8) +
       'UNIT'.padStart(12) +
       'TOTAL'.padStart(12) +
       '\n');

  push( boldOff());
  push( hr());

  invoice.items.forEach((it, idx) => {
    const lineNo = idx + 1;

    push(`${lineNo}. ${it.product?.name || ''}\n`);

    const qty   = Number(it.quantity || 0);
    const price = Number(it.price || 0);
    const total = Number(it.total || qty * price);

    push(colLine(
      qty,
      price.toFixed(2),
      total.toFixed(2)
    ));
  });
  push( hr());

  /* =====================
     Totals
  ====================== */
  const total = Number(invoice.total_price || 0);
  const net = Number(invoice.net_price || 0);
  const discount = Number(total - net || 0);

  push(totalsLine("Total:", total.toFixed(2)));

  if (discount > 0) {
    push(totalsLine("Discount:", discount.toFixed(2)));
  }

  push(boldOn());
  push(totalsLine("Net Total:", net.toFixed(2)));
  push(boldOff());

  const itemCount = invoice.items?.length || 0;
  push(align(0));
  push(`Invoiced Items: ${itemCount}\n`);

  push( feed(2));

    /* =====================
    QR Code + Footer
    ===================== */

    // Reset printer state
    push( ESC + '@');

    // Center QR
    push( align(1));

    // ---- QR CODE ----

    // Select model 2
    push( GS + '(k' + '\x04\x00' + '1A' + '\x02\x00');

    // Set size (module size)
    push( GS + '(k' + '\x03\x00' + '1C' + '\x06');

    // Error correction level (L = 48)
    push( GS + '(k' + '\x03\x00' + '1E' + '\x30');

    // Store QR data
    const data = String(invoice.invoice_number);
    push( GS + '(k'
    + String.fromCharCode(data.length + 3)
    + '\x00'
    + '1P0'
    + data
    );

    // Print QR
    push( GS + '(k' + '\x03\x00' + '1Q0');
    // Footer text
    if (cfg.receipt_footer_text) {
    push( feed(1));
    push( cfg.receipt_footer_text + '\n');
    }

    // Final feed before cut
    push( feed(3));

  return out
  .map(b => String.fromCharCode(b))
  .join('');
}
